/**
 * *Question settings* (`specs/M4-V8-forms.md` §2.3).
 *
 * Measured: a **300 px** panel with *Question display* · *Options order* · *Pre-fill value* ·
 * *Hide question* · **Required**, each a switch `[cap:sandbox-question-settings.png]`. Required
 * is the one that matters — it is monday's `mandatory` (q295).
 */
import type { FormQuestion } from '@elizmonday/shared';
import { useOverlayDismiss } from '@elizmonday/ui';

export function QuestionSettingsPanel({
  question,
  onChange,
  onClose,
  'data-testid': testId = 'question-settings-panel',
}: {
  question: FormQuestion;
  onChange(patch: { required?: boolean; visible?: boolean }): void;
  onClose(): void;
  /**
   * The id the panel's root renders. It is a prop so the **call site** can state it too: the
   * coverage ledger reads literal `data-testid` attributes off JSX, and a component invocation
   * carrying only handlers was an element nothing could ever name (P-189).
   */
  'data-testid'?: string;
}) {
  /*
   * Round 7b: the panel is a 300 px column on a desktop and a bottom sheet on a phone
   * (`mobile.css`), and either way it has to be dismissible — the shared P-013 contract, so
   * Escape closes it and focus returns to the control that opened it.
   */
  useOverlayDismiss({ onClose });
  return (
    <aside className="ez-question-settings" aria-label="Question settings" data-testid={testId}>
      <header>
        <h2>Question settings</h2>
        <button type="button" aria-label="Close question settings" data-testid="question-panel-close" onClick={onClose}>×</button>
      </header>
      <label>
        <span>Pre-fill value</span>
        <input type="checkbox" disabled title="Coming with M4.V8b" data-testid="qs-prefill" />
      </label>
      <label>
        <span>Hide question</span>
        <input
          type="checkbox"
          data-testid="qs-hidden"
          checked={!question.visible}
          onChange={(event) => onChange({ visible: !event.target.checked })}
        />
      </label>
      <label>
        <span>Required</span>
        <input
          type="checkbox"
          role="switch"
          data-testid="qs-required"
          checked={question.required}
          onChange={(event) => onChange({ required: event.target.checked })}
        />
      </label>
    </aside>
  );
}
